import { Check } from 'lucide-react';
import type { EFileDetail } from '@tabservice/tabfile-client';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

type TimelineEntry = NonNullable<EFileDetail['timeline']>[number];

/**
 * What has happened to this e-file, oldest first, so the story reads top down.
 *
 * Drawn with the wizard rail's marks, lines and type, because a customer reads both as "the
 * timeline" and two different drawings of the same idea on two pages of one product is the
 * kind of difference somebody notices without being able to say what is wrong. A step that
 * happened is a filled circle with a tick in it; the step in hand is a hollow ring. The event
 * comes first and its time sits under it, the way each stage of the rail is stamped.
 *
 * While the e-file is still moving the feed ends on that hollow ring rather than on the last
 * thing that happened, and `waiting` says what it is waiting for.
 */
export function FilingTimeline({
  entries,
  inFlight = false,
  waiting = null,
}: {
  entries: TimelineEntry[];
  inFlight?: boolean;
  /** One sentence for the open step: what is being waited for, and when that wait ends. */
  waiting?: string | null;
}) {
  if (entries.length === 0) {
    return <p className="text-base text-muted-foreground">No activity on this filing yet.</p>;
  }

  const rows: { key: string; label: string; at: string | null; detail?: string | null; open: boolean }[] = entries.map(
    (entry) => ({
      key: `${entry.at}-${entry.event}`,
      label: eventLabel(entry.event),
      at: entry.at,
      detail: entry.detail,
      open: false,
    }),
  );

  if (inFlight) {
    rows.push({ key: 'in-flight', label: 'In progress', at: null, detail: waiting, open: true });
  }

  return (
    <ol className="flex flex-col">
      {rows.map((row, index) => {
        const last = index === rows.length - 1;
        return (
          <li key={row.key} className="flex gap-3">
            {/* The mark and the line under it, so the events read as one column. */}
            <div className="flex flex-col items-center">
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                  row.open
                    ? 'bg-surface shadow-[inset_0_0_0_4px_var(--ts-primary)]'
                    : 'bg-primary text-primary-foreground',
                )}
              >
                {row.open ? null : <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              {!last ? (
                <span
                  aria-hidden="true"
                  className="my-1.5 w-0.5 flex-1 rounded-full bg-[color:var(--ts-primary-light)]"
                />
              ) : null}
            </div>
            <div className={cn('min-w-0 pt-0.5 pb-6', last && 'pb-0')}>
              <p className={cn('text-base text-gray-3', row.open && 'font-semibold text-foreground')} aria-current={row.open ? 'step' : undefined}>
                {row.label}
              </p>
              {row.at ? <p className="mt-1 text-sm text-muted-foreground tabular-nums">{formatDateTime(row.at)}</p> : null}
              {row.detail ? <p className="mt-0.5 text-base text-muted-foreground">{row.detail}</p> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** The audit event names are for machines; the feed reads in words. */
const EVENT_LABELS: Record<string, string> = {
  'efile.create': 'E-file created',
  'efile.created': 'E-file created',
  'transmission.acknowledged': 'IRS answered',
  'record.fix': 'Fix queued',
  'record.reopen': 'Record reopened',
  'upload.create': 'File uploaded',
  'transmission.sent': 'Sent to the IRS',
  'transmission.failed': 'Could not be sent',
  'efile.acknowledged': 'IRS answered',
  'efile.transmitted': 'Sent to the IRS',
  'efile.failed': 'Could not be sent',
  'record.fix.queued': 'Fix queued',
  'record.fix.submitted': 'Fix sent in a correction',
  'record.reopened': 'Record reopened',
  'correction.created': 'Correction filed',
  'replacement.created': 'Replacement filed',
  'upload.created': 'File uploaded',
  'upload.checked': 'File checked',
};

function eventLabel(event: string): string {
  if (EVENT_LABELS[event]) return EVENT_LABELS[event];
  const words = event.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
